import { toDateOnlyIso } from "./dataMappers";

const CAIRO_TZ = "Africa/Cairo";
const WEEKLY_REST_TYPE = "راحة أسبوعية";

/** Calendar date in Egypt (YYYY-MM-DD). */
export function todayDateOnlyIso(now = new Date()) {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: CAIRO_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);
}

function emptyMatchers() {
  return {
    employeeIds: new Set(),
    userIds: new Set(),
    codes: new Set(),
  };
}

function addMatcherIdentity(matchers, identity) {
  if (identity.employeeId) matchers.employeeIds.add(String(identity.employeeId));
  if (identity.userId) matchers.userIds.add(String(identity.userId));
  if (identity.employeeCode) matchers.codes.add(String(identity.employeeCode));
}

function isApprovedLeaveRequest(request) {
  if (!request || request.status !== "approved") return false;
  if (request._source === "permit" || request.type === "إذن") return false;
  return (
    request._source === "leave" ||
    request.type?.includes("إجازة") ||
    request.type?.includes("مرضية")
  );
}

function leaveCoversDate(request, dateIso) {
  const start = toDateOnlyIso(request.startDate);
  const end = toDateOnlyIso(request.endDate) || start;
  if (!start || !dateIso) return false;
  return start <= dateIso && dateIso <= end;
}

export function isCurrentApprovedLeave(request, dateIso = todayDateOnlyIso()) {
  return isApprovedLeaveRequest(request) && leaveCoversDate(request, dateIso);
}

export function collectCurrentlyOnLeaveMatchers(
  requests,
  dateIso = todayDateOnlyIso(),
) {
  const matchers = emptyMatchers();

  for (const request of requests || []) {
    if (!isCurrentApprovedLeave(request, dateIso)) continue;
    addMatcherIdentity(matchers, request);
  }

  return matchers;
}

function timesheetStatusKey(status) {
  return String(status || "").toLowerCase();
}

function timesheetCountsForWork(status) {
  const key = timesheetStatusKey(status);
  return key !== "cancelled" && key !== "rejected";
}

function timesheetCountsForRest(status) {
  const key = timesheetStatusKey(status);
  return key === "approved" || key === "pending";
}

function timesheetEmployeeIdentity(emp) {
  return {
    employeeId: emp.employeeId || emp.id || null,
    userId: emp.userId || null,
    employeeCode: emp.employeeCode || emp.code || null,
    employeeName: emp.employeeName || emp.fullName || emp.name || "",
  };
}

function identityKey(identity) {
  if (identity.employeeId) return `id:${identity.employeeId}`;
  if (identity.userId) return `user:${identity.userId}`;
  if (identity.employeeCode) return `code:${identity.employeeCode}`;
  if (identity.employeeName) return `name:${identity.employeeName}`;
  return null;
}

function dayTypeOf(entry) {
  return String(entry?.dayType || "").toUpperCase();
}

/**
 * REST on one sheet + WORK (coverage) on another sheet for the same day
 * is not weekly rest — the employee is covering.
 */
export function collectCurrentlyOnWeeklyRestMatchers(
  timesheets,
  dateIso = todayDateOnlyIso(),
) {
  const workKeys = new Set();
  const restIdentities = [];

  for (const timesheet of timesheets || []) {
    const status = timesheet.status;
    for (const emp of timesheet.employees || []) {
      const identity = timesheetEmployeeIdentity(emp);
      const key = identityKey(identity);
      for (const entry of emp.entries || []) {
        if (toDateOnlyIso(entry.date) !== dateIso) continue;
        const dayType = dayTypeOf(entry);
        if (dayType === "WORK" && timesheetCountsForWork(status) && key) {
          workKeys.add(key);
        }
        if (dayType === "REST" && timesheetCountsForRest(status)) {
          restIdentities.push(identity);
        }
      }
    }
  }

  const matchers = emptyMatchers();
  for (const identity of restIdentities) {
    const key = identityKey(identity);
    if (key && workKeys.has(key)) continue;
    addMatcherIdentity(matchers, identity);
  }
  return matchers;
}

export function collectWeeklyRestCalendarDays(timesheets = []) {
  const byDate = new Map();

  for (const timesheet of timesheets || []) {
    const status = timesheet.status;
    for (const emp of timesheet.employees || []) {
      const identity = timesheetEmployeeIdentity(emp);
      const key = identityKey(identity);
      for (const entry of emp.entries || []) {
        const date = toDateOnlyIso(entry.date);
        if (!date || !key) continue;
        let bucket = byDate.get(date);
        if (!bucket) {
          bucket = { workKeys: new Set(), rest: new Map() };
          byDate.set(date, bucket);
        }
        const dayType = dayTypeOf(entry);
        if (dayType === "WORK" && timesheetCountsForWork(status)) {
          bucket.workKeys.add(key);
        }
        if (dayType === "REST" && timesheetCountsForRest(status)) {
          bucket.rest.set(key, identity);
        }
      }
    }
  }

  const entries = [];
  for (const [date, bucket] of byDate) {
    for (const [key, identity] of bucket.rest) {
      if (bucket.workKeys.has(key)) continue;
      entries.push({
        date,
        employee: identity.employeeName,
        employeeId: identity.employeeId,
        employeeCode: identity.employeeCode,
        type: WEEKLY_REST_TYPE,
      });
    }
  }
  return entries;
}

function matchesCurrentLeave(emp, matchers) {
  if (!emp || !matchers) return false;
  if (emp.id && matchers.employeeIds.has(String(emp.id))) return true;
  if (emp.userId && matchers.userIds.has(String(emp.userId))) return true;
  if (emp.code && matchers.codes.has(String(emp.code))) return true;
  return false;
}

export function employeeIsCurrentlyOnLeave(emp, matchers) {
  if (!emp || emp.status === "terminated") return false;
  if (emp.status === "on-leave") return true;
  return matchesCurrentLeave(emp, matchers);
}

export function employeeIsCurrentlyOnWeeklyRest(emp, restMatchers) {
  if (!emp || emp.status === "terminated") return false;
  return matchesCurrentLeave(emp, restMatchers);
}

export function employeeIsCurrentlyOff(emp, leaveMatchers, restMatchers) {
  return (
    employeeIsCurrentlyOnLeave(emp, leaveMatchers) ||
    employeeIsCurrentlyOnWeeklyRest(emp, restMatchers)
  );
}

export function findCurrentLeaveForEmployee(
  emp,
  requests,
  dateIso = todayDateOnlyIso(),
) {
  if (!emp) return null;
  return (
    (requests || []).find((request) => {
      if (!isCurrentApprovedLeave(request, dateIso)) return false;
      if (emp.id && String(request.employeeId) === String(emp.id)) return true;
      if (emp.userId && String(request.userId) === String(emp.userId)) {
        return true;
      }
      if (emp.code && String(request.employeeCode) === String(emp.code)) {
        return true;
      }
      return false;
    }) || null
  );
}

export function findCurrentAbsenceForEmployee(
  emp,
  requests,
  timesheets,
  dateIso = todayDateOnlyIso(),
) {
  const leave = findCurrentLeaveForEmployee(emp, requests, dateIso);
  if (leave) return leave;
  if (
    employeeIsCurrentlyOnWeeklyRest(
      emp,
      collectCurrentlyOnWeeklyRestMatchers(timesheets, dateIso),
    )
  ) {
    return {
      type: WEEKLY_REST_TYPE,
      startDate: dateIso,
      endDate: dateIso,
      _source: "weekly-rest",
    };
  }
  return null;
}

export function countCurrentlyOnLeaveEmployees(
  employees,
  requests,
  timesheets = [],
  dateIso = todayDateOnlyIso(),
) {
  const leaveMatchers = collectCurrentlyOnLeaveMatchers(requests, dateIso);
  const restMatchers = collectCurrentlyOnWeeklyRestMatchers(
    timesheets,
    dateIso,
  );
  return (employees || []).filter((emp) =>
    employeeIsCurrentlyOff(emp, leaveMatchers, restMatchers),
  ).length;
}
